import React, { useEffect, useState } from "react";
import {
  Button,
  Table,
  Space,
  Typography,
  App,
  Popconfirm,
  Flex,
  Grid,
  Tooltip,
  Drawer,
} from "antd";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchSuppliersList, deleteSupplier } from "../suppliersThunk";

import { setSelectedSupplier, setIsEditSupplier } from "../suppliersSlice";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { useIsAdminUser } from "../../../../../lib/accessList";

const { Title } = Typography;

const SupplierList = () => {
  const { modal } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authInfo = useSelector((state) => state.auth?.auth_info);
  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;

  const isAdminUser = useIsAdminUser();

  const { suppliers, loading } = useSelector((state) => state.suppliers);
  const [helpOpen, setHelpOpen] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  useEffect(() => {
    dispatch(fetchSuppliersList());
  }, [dispatch]);

  const handleEdit = (record) => {
    dispatch(setSelectedSupplier(record));
    dispatch(setIsEditSupplier(true));
    navigate("/purchase/addNewSupplier");
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };
  const handleDelete = async (id) => {
    try {
      const res = await dispatch(deleteSupplier(id)).unwrap();
      modal.success({
        title: res?.msg || "Supplier deleted successfully",
      });
      dispatch(fetchSuppliersList());
    } catch (err) {
      modal.error({
        title: err.message || "Failed to delete supplier",
      });
    }
  };

  const columns = [
    {
      title: "#",
      render: (_, __, index) => index + 1,
      width: 50,
    },
    {
      title: "Code",
      dataIndex: "acc_code",
    },
    {
      title: "Supplier Name",
      dataIndex: "acc_name",
    },
    {
      title: "Institution",
      dataIndex: "institution_name",
    },
    {
      title: "Address",
      dataIndex: "address",
    },
    {
      title: "Contact No",
      dataIndex: "contact_no",
    },
    {
      title: "Area",
      dataIndex: "location_name",
    },
    {
      title: "Opening Due",
      dataIndex: "opening_balance",
    },
    {
      title: "Entry By",
      dataIndex: "full_name",
    },
    {
      title: "Action",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            disabled={!(isAdminUser || canEdit)}
            onClick={() => handleEdit(record)}
            icon={<EditOutlined />}
          />

          <Popconfirm
            title="Are you sure to delete this supplier?"
            disabled={!(isAdminUser || canDelete)}
            onConfirm={() => handleDelete(record.acc_id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger disabled={!(isAdminUser || canDelete)} icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
  <Flex justify="space-between" align="center">

    {/* LEFT SIDE */}
    <Flex align="center" gap={8}>
      <Button
        type="text"
        icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />}
        onClick={() => navigate(-1)}
        data-testid="suppliers-back-button"
      />
      <Title level={!screens.md ? 4 : 2} style={{ margin: 0 }}>
        Suppliers
      </Title>
    </Flex>

    {/* RIGHT SIDE */}
    <Flex align="center" gap={6}>
      <Tooltip title="Help">
        <Button
          type="text"
          icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
          onClick={() => setHelpOpen(true)}
        />
      </Tooltip>

      <Button
        type="primary"
        className="traic-button"
        onClick={() => navigate("/purchase/addNewSupplier")}
      >
        Add New
      </Button>
    </Flex>

  </Flex>
      <Table
        rowKey="acc_id"
        loading={loading}
        dataSource={suppliers}
        columns={columns}
        scroll={{ x: "max-content" }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
        onChange={handleTableChange}
        bordered
      />
      <Drawer
  title="Supplier Management Help"
  open={helpOpen}
  onClose={() => setHelpOpen(false)}
  width={430}
  styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
>
  <div style={{ lineHeight: 1.8 }}>
    <h2
      style={{
        fontSize: "18px",
        fontWeight: 600,
        marginBottom: "12px",
      }}
    >
      Supplier Management
    </h2>

    <p style={{ color: "#555", marginBottom: "16px" }}>
      The Supplier Management module is used to manage supplier accounts,
      purchase-related party details, contact information, and supplier
      transaction workflows within the ERP Purchase system.
    </p>

    <div
      style={{
        background: "#f5f5f5",
        padding: "14px",
        borderRadius: "8px",
        marginBottom: "16px",
      }}
    >
      <strong>Available Actions</strong>

      <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
        <li>View supplier records and account details</li>
        <li>Create new supplier entries</li>
        <li>Edit supplier information and account data</li>
        <li>Delete supplier records if permitted</li>
        <li>Manage supplier addresses and contact numbers</li>
        <li>Track supplier opening balances and dues</li>
        <li>View institution and area information</li>
        <li>Monitor supplier entry history and creator details</li>
        <li>Search and manage supplier transaction workflows</li>
      </ol>
    </div>

    <div
      style={{
        background: "#fafafa",
        border: "1px solid #eee",
        padding: "14px",
        borderRadius: "8px",
      }}
    >
      <strong>Important Notes</strong>

      <ul
        style={{
          paddingLeft: "18px",
          marginTop: "10px",
          color: "#666",
        }}
      >
        <li>Supplier records are linked with purchase transactions.</li>
        <li>Opening balances affect supplier accounting reports.</li>
        <li>Role permissions control edit and delete actions.</li>
        <li>Supplier contact details help purchase communication workflows.</li>
        <li>Deleted suppliers may affect linked purchase history.</li>
      </ul>
    </div>
  </div>
</Drawer>
    </div>
  );
};

export default SupplierList;
